import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { httpsCallable } from 'firebase/functions';
import {
  IconBolt,
  IconBrandStrava,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconDownload,
} from '@tabler/icons-react';
import { functions } from '../firebase';
import { groupTemplatesByStyle } from '../components/StylePicker';
import { startStravaConnect } from '../utils/stravaConnect';
import StyleAhaCard from '../components/StyleAhaCard';
import HowItWorksConnectCard from '../components/HowItWorksConnectCard';
import Footer from '../components/Footer';
import MythicMapsLogo from '../assets/logos/80ies_TdF_Logo.png';
import StravaConnectButton from '../assets/strava/btn_strava_connect_with_orange.svg';
import sakurasPreview from '../assets/images/sakuras_maps_example.png';
import './ChallengePage.css';
import './MapGenerationPage.css';
import './InviteLandingPage.css';

// CommonJS on purpose (shared with scripts/seo-prerender.js), same import style as /maps.
const marketingSeo = require('../seo/marketingSeo');

// Firebase callables surface either the backend's own message ("This invite link is no longer
// valid.") or an opaque code ("internal", "not-found"). A code means nothing to a cold visitor,
// so only pass through something that reads like a sentence.
const readableError = (err, fallback) => {
  const message = err?.message || '';
  return /\s/.test(message) ? message : fallback;
};

// Landing page for a referral link (/invite/:inviteId): the /maps landing engine, personalised
// by the inviter and the invited style. The visitor is a friend of a member, arriving from a
// personal message with real trust in the sender but no idea what Mythic Maps is, so the page
// leads with the personal framing (their friend's face and name) and then runs the full /maps
// funnel underneath it: the real aha card featuring the invited style, an offer strip, the
// /maps how-it-works and pricing blocks, the shared FAQ behind three invite-specific questions,
// and one Strava path.
//
// Behind the button: connecting runs acceptReferralInvite, which generates a map of the
// INVITEE'S OWN ride (the public Strava activity the inviter pasted into ReferralInviteBuilder)
// and hands off to ManagePostPage (/manage-post/:requestId), which owns the polished
// generating -> finished-map experience. The +5 diamonds land server-side when that map
// completes (onReferralGenerationComplete), for referrals only: gift generations are tagged
// isGiftGeneration and earn nobody a reward, which is why the offer strip splits its copy.
// An existing account on a non-gift invite is blocked, and gets the aha card in its default
// mode (their own latest map) plus a "make my own map" path instead of a dead end.
//
// Connect stays a direct startStravaConnect call: App.js resolveJoinVariant would route
// /invite/* into the generic three-product join modal, which discards this page's context.
function InviteLandingPage({ user }) {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const posthog = usePostHog();

  const [invite, setInvite] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [phase, setPhase] = useState('idle'); // idle | accepting | blocked | error
  const [error, setError] = useState('');
  const [styleGroups, setStyleGroups] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const acceptedRef = useRef(false);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    posthog?.capture('invite_landing_viewed', {
      page: 'invite_landing',
      invite_id: inviteId,
      is_authenticated: !!user,
    });
  }, [posthog, inviteId, user]);

  // Public preview of the invite.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const getInvite = httpsCallable(functions, 'getReferralInvite');
        const res = await getInvite({ inviteId });
        if (!cancelled) { setInvite(res.data); setLoadingInvite(false); }
      } catch (err) {
        if (!cancelled) {
          setError(readableError(err, t('inviteLanding.loadError', 'This invite link is no longer valid.')));
          setPhase('error');
          setLoadingInvite(false);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteId]);

  // Best-effort: fetch the public style catalog (same call as the /maps page) to find the
  // invited style's artwork for the hero and the aha mock. Failures degrade silently.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const getTemplates = httpsCallable(functions, 'getPromptTemplates');
        const result = await getTemplates();
        if (cancelled) return;
        const list = Array.isArray(result.data?.templates)
          ? result.data.templates.filter((tpl) => tpl.enabled !== false && !tpl.adminOnly)
          : [];
        setStyleGroups(groupTemplatesByStyle(list));
      } catch (err) {
        // No artwork; the gradient hero and the card's generic mock pair cover this.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Once signed in, accept the invite (once), then hand off to ManagePostPage.
  useEffect(() => {
    if (!user || !inviteId || acceptedRef.current) return;
    acceptedRef.current = true;
    setPhase('accepting');
    (async () => {
      try {
        const accept = httpsCallable(functions, 'acceptReferralInvite');
        const res = await accept({ inviteId });
        const requestId = res.data?.requestId;

        if (requestId) {
          // The invitee's +5 is granted server-side when the referral map completes
          // (onReferralGenerationComplete); nothing to do here but hand off.
          navigate(`/manage-post/${requestId}`, { replace: true });
          return;
        }

        // No generation (existing account on a non-gift invite).
        posthog?.capture('invite_landing_blocked', { page: 'invite_landing', invite_id: inviteId });
        setPhase('blocked');
      } catch (err) {
        console.error('acceptReferralInvite failed:', err);
        setError(readableError(err, t('inviteLanding.acceptError', 'We could not claim this map.')));
        setPhase('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, inviteId, navigate]);

  const inviterName = invite?.inviterName || t('inviteLanding.friendFallback', 'A friend');
  const isGift = invite?.isGift === true;

  // The invited style's group, matched on the base name the invite stores
  // (createReferralInvite runs getStyleBaseName before writing styleName).
  const styleGroup = useMemo(() => {
    if (!invite?.styleName || styleGroups.length === 0) return null;
    return styleGroups.find((g) => g.name === invite.styleName) || null;
  }, [invite, styleGroups]);

  // Landscape example artwork for the hero banner, preferring the invited dimension.
  const exampleImageUrl = useMemo(() => {
    if (!styleGroup) return '';
    return invite.mapDimension === '3d'
      ? (styleGroup.exampleImageUrl3D || styleGroup.exampleImageUrl2D || styleGroup.exampleImageUrl || '')
      : (styleGroup.exampleImageUrl2D || styleGroup.exampleImageUrl3D || styleGroup.exampleImageUrl || '');
  }, [invite, styleGroup]);

  // The logged-out aha "after": the invited style's portrait example, so the friend sees the
  // exact style they were invited into. The "before" stays the card's default portrait 3D map,
  // which is why we prefer the portrait 3D art here. Memoized because the card's rotation
  // interval keys off this array's identity; a single entry stays static, which is what we want.
  // Undefined falls back to the card's generic four-style rotation.
  const mockAfterUrls = useMemo(() => {
    const portrait = styleGroup
      && (styleGroup.portraitExampleImageUrl3D || styleGroup.portraitExampleImageUrl);
    return portrait ? [portrait] : undefined;
  }, [styleGroup]);

  const ahaAnalyticsProps = useMemo(
    () => ({ page: 'invite_landing', invite_id: inviteId }),
    [inviteId]
  );

  // FAQ: three invite-specific questions on top, then exactly the /maps FAQ (single copy source
  // in marketingSeo). Built in a memo rather than at module scope because two of the three
  // interpolate the inviter's name. The route is dynamic and noindexed, so no JSON-LD here.
  const inviteFaqs = useMemo(() => ([
    {
      qKey: 'inviteLanding.faqWhatQ',
      q: 'What exactly did {{name}} send me?',
      aKey: 'inviteLanding.faqWhatA',
      a: 'A free art map of one of your own rides. {{name}} picked the activity and the {{style}} style, then connecting Strava lets us render your route and paint it. The map is yours: on your profile, your Strava, and your camera roll.',
      vars: { name: inviterName, style: invite?.styleName || '' },
    },
    {
      qKey: 'inviteLanding.faqPrivacyQ',
      q: 'Does {{name}} see my activities?',
      aKey: 'inviteLanding.faqPrivacyA',
      a: '{{name}} started this from a link to one of your Strava activities, the same way anyone with the link can open it. Connecting here gives {{name}} nothing new: not your account, not your other activities, not your maps.',
      vars: { name: inviterName },
    },
    {
      qKey: 'inviteLanding.faqCostQ',
      q: 'What does it cost?',
      aKey: 'inviteLanding.faqCostA',
      a: 'Nothing. Joining is free and needs no card. Art maps run on diamonds, one diamond per map, and you earn them free from challenges, referrals, and sharing your maps.',
    },
    ...marketingSeo.MAPS_FAQS,
  ]), [inviterName, invite]);

  const handleStravaCta = () => {
    posthog?.capture('invite_landing_strava_clicked', { page: 'invite_landing', invite_id: inviteId });
    startStravaConnect(`/invite/${inviteId}`);
  };

  // Separate event so the "How it works" step-1 card is measurable on its own; the
  // delegate below still fires the page's invite_landing_strava_clicked event.
  const handleHiwConnect = () => {
    posthog?.capture('invite_landing_hiw_strava_clicked', { page: 'invite_landing', invite_id: inviteId });
    handleStravaCta();
  };

  const handleCreateClick = () => {
    posthog?.capture('invite_landing_create_clicked', { page: 'invite_landing', invite_id: inviteId });
    navigate('/create');
  };

  if (loadingInvite) {
    return (
      <div className="invite-landing-page">
        <div className="invite-landing-message">{t('inviteLanding.loading', 'Loading your invite…')}</div>
        <Footer />
      </div>
    );
  }

  if (phase === 'error' && !invite) {
    return (
      <div className="invite-landing-page">
        <div className="invite-landing-message invite-error">{error}</div>
        <Footer />
      </div>
    );
  }

  const dimensionLabel = invite.mapDimension === '3d' ? '3D' : '2D';
  const orientationLabel = invite.mapOrientation === 'portrait'
    ? t('inviteLanding.orientationPortrait', 'portrait')
    : t('inviteLanding.orientationLandscape', 'landscape');
  // Keep the scrim: an inline background-image replaces the stylesheet's gradient outright, so
  // compose both here or the title contrast depends entirely on the artwork behind it.
  const heroStyle = exampleImageUrl
    ? { backgroundImage: `linear-gradient(160deg, rgba(255, 255, 255, 0.22), rgba(0, 0, 0, 0.32)), url(${exampleImageUrl})` }
    : undefined;
  const blocked = phase === 'blocked';

  return (
    <div className="invite-landing-page">
      <div className="invite-hero" style={heroStyle} role="presentation" />

      <div className="invite-lockup">
        {invite.inviterPhoto && (
          <>
            <span className="invite-lockup-chip invite-lockup-avatar">
              <img src={invite.inviterPhoto} alt={inviterName} />
            </span>
            <span className="invite-lockup-x" aria-hidden="true">×</span>
          </>
        )}
        <span className="invite-lockup-chip">
          <img src={MythicMapsLogo} alt="Mythic Maps" />
        </span>
      </div>

      <div className="invite-content">
        <h1 className="invite-title">
          {isGift
            ? t('inviteLanding.titleGift', '{{name}} gifted you a map', { name: inviterName })
            : t('inviteLanding.titleMade', '{{name}} made you a map', { name: inviterName })}
        </h1>
        <p className="invite-sub">
          {t('inviteLanding.subLine', 'A {{style}} {{dimension}} {{orientation}} map, ready to generate on your Strava activity.', {
            style: invite.styleName,
            dimension: dimensionLabel,
            orientation: orientationLabel,
          })}
        </p>
      </div>

      {/* The /maps aha engine, invite-aware: logged out it mocks the invited style, so a cold
          visitor sees what the gift actually is before deciding to connect. Never mounted while
          the accept is in flight, or it would start a competing /maps generation moments before
          we hand off to /manage-post. */}
      <div className="section-dark">
        <div className="section-inner">
          {phase === 'accepting' && (
            <div className="invite-status">
              <div className="invite-spinner" />
              <p>{t('inviteLanding.accepting', 'Setting up your map…')}</p>
            </div>
          )}
          {blocked && (
            <div className="invite-blocked">
              <p>{t('inviteLanding.blockedExisting', 'It looks like you already have a Mythic Maps account, so this free map is just for new members.')}</p>
              <p>{t('inviteLanding.blockedOwn', 'You can make your own map any time.')}</p>
            </div>
          )}
          {phase === 'error' && invite && <p className="invite-error">{error}</p>}
          {(!user || blocked) && (
            <StyleAhaCard
              user={user}
              onStravaConnect={handleStravaCta}
              styleGroups={styleGroups}
              featuredStyleName={blocked ? undefined : invite.styleName}
              mockAfterUrls={mockAfterUrls}
              analyticsSource="invite_aha"
              analyticsProps={ahaAnalyticsProps}
            />
          )}
        </div>
      </div>

      {/* Offer strip: what this specific invite adds on top of the free product. */}
      <div className="section-darker">
        <div className="section-inner">
          <div className="challenge-section">
            <div className="section-header-centered">
              <h2>{t('inviteLanding.offerTitle', "What {{name}}'s invite gets you", { name: inviterName })}</h2>
            </div>
            <ul className="invite-offer-lines">
              <li>
                <IconCheck size={17} />
                <span>
                  {t('inviteLanding.offerMap', 'A {{style}} art map of your own ride, the one {{name}} picked', {
                    style: invite.styleName,
                    name: inviterName,
                  })}
                </span>
              </li>
              <li>
                <IconCheck size={17} />
                <span>
                  {isGift
                    ? t('inviteLanding.offerGift', '{{name}} already paid for this one, so it costs you nothing.', { name: inviterName })
                    : t('inviteLanding.offerDiamonds', '5 diamonds when your map is ready, enough for 5 more art maps. Everyone else earns these through challenges and sharing.')}
                </span>
              </li>
              <li>
                <IconCheck size={17} />
                <span>
                  {t('inviteLanding.offerEveryActivity', 'Every activity after this one, drawn the same way, whenever you want')}
                </span>
              </li>
            </ul>
            <p className="invite-offer-availability">
              {t('inviteLanding.offerAvailability', 'This map is reserved for you. Mythic Maps itself is always free.')}
            </p>
          </div>
        </div>
      </div>

      {/* How it works: replicated from /maps (same classes, assets, and copy keys). */}
      <div className="section-gradient">
        <div className="section-inner">
          <div className="challenge-section">
            <div className="section-header-centered">
              <h2>{t('mapGenPage.howItWorks', 'How it works')}</h2>
            </div>
            <div className="hiw-cards">
              <HowItWorksConnectCard user={user} onConnect={handleHiwConnect} />

              <div className="hiw-card">
                <div className="hiw-visual hiw-visual-auto">
                  <div className="hiw-auto-mock">
                    <div className="hiw-auto-mock-label">
                      <IconBolt size={16} />
                      <span>{t('mapGenPage.hiw2MockLabel', 'Auto-generation')}</span>
                    </div>
                    <div className="hiw-auto-mock-toggle">
                      <span className="hiw-auto-mock-toggle-on">ON</span>
                      <span className="hiw-auto-mock-switch" aria-hidden="true">
                        <span className="hiw-auto-mock-knob" />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="hiw-content">
                  <div className="hiw-badge">2</div>
                  <h3>{t('mapGenPage.hiw2Title', 'Turn on auto-generation')}</h3>
                  <p>{t('mapGenPage.hiw2Desc', 'Recommended. From this moment on, every Strava activity becomes a Mythic Map.')}</p>
                </div>
              </div>

              <div className="hiw-card">
                <div className="hiw-visual hiw-visual-strava">
                  <div className="hiw-strava-mock">
                    <img
                      src={sakurasPreview}
                      alt=""
                      className="hiw-strava-mock-photo"
                    />
                    <div className="hiw-strava-mock-body">
                      <div className="hiw-strava-mock-row">
                        <div className="hiw-strava-mock-avatar" aria-hidden="true" />
                        <div className="hiw-strava-mock-meta">
                          <span className="hiw-strava-mock-name">{t('mapGenPage.hiw3MockUser', 'You')}</span>
                          <span className="hiw-strava-mock-time">{t('mapGenPage.hiw3MockTime', 'Morning ride · just now')}</span>
                        </div>
                        <IconBrandStrava size={18} className="hiw-strava-mock-logo" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="hiw-content">
                  <div className="hiw-badge">3</div>
                  <h3>{t('mapGenPage.hiw3Title', 'The map lands on your activity')}</h3>
                  <p>{t('mapGenPage.hiw3Desc', 'Auto-upload replaces your Strava cover photo with the artwork, plus the transition video from your Strava map into the Mythic Map.')}</p>
                </div>
              </div>

              <div className="hiw-card">
                <div className="hiw-visual hiw-visual-rewards">
                  <div className="hiw-reward-mock">
                    <div className="hiw-reward-icon">
                      <IconDownload size={24} />
                    </div>
                    <div className="hiw-reward-text">
                      <span className="hiw-reward-title">{t('mapGenPage.hiw4Mock', 'Saved to your devices')}</span>
                      <span className="hiw-reward-subtitle">{t('mapGenPage.hiw4MockSub', 'Profile, Strava, camera roll')}</span>
                    </div>
                  </div>
                </div>
                <div className="hiw-content">
                  <div className="hiw-badge">4</div>
                  <h3>{t('mapGenPage.hiw4Title', 'Keep everything')}</h3>
                  <p>{t('mapGenPage.hiw4Desc', 'Your maps stay on your profile, your Strava, and your camera roll.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Free to create: the /maps pricing block with the invite called out. */}
      <div className="section-darker">
        <div className="section-inner">
          <div className="challenge-section">
            <div className="section-header-centered">
              <h2>{t('mapGenPage.pricingTitle', 'Free to create')}</h2>
              <p className="section-description">
                {t('inviteLanding.pricingDesc', "Art maps run on diamonds, and you earn them free from challenges, referrals, and sharing your maps. {{name}}'s invite covers your first one.", { name: inviterName })}
              </p>
            </div>

            <div className="map-gen-stats">
              <div className="map-gen-stat">
                <span className="map-gen-stat-value">$0</span>
                <span className="map-gen-stat-label">{t('inviteLanding.statJoin', 'To join, no card needed')}</span>
              </div>
              <div className="map-gen-stat">
                <span className="map-gen-stat-value">40+</span>
                <span className="map-gen-stat-label">{t('mapGenPage.statStyles', 'Styles in production, growing monthly')}</span>
              </div>
              <div className="map-gen-stat">
                <span className="map-gen-stat-value">50k+</span>
                <span className="map-gen-stat-label">{t('mapGenPage.statMaps', 'Maps generated and counting')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="section-default">
        <div className="section-inner">
          <div className="challenge-section">
            <div className="section-header-centered">
              <h2>{t('mapGenPage.faqTitle', 'Frequently asked questions')}</h2>
            </div>
            <div className="faq-list">
              {inviteFaqs.map((faq, index) => (
                <div
                  key={index}
                  className={`faq-item ${openFaq === index ? 'open' : ''}`}
                >
                  <button
                    type="button"
                    className="faq-question"
                    onClick={() => {
                      const isOpening = openFaq !== index;
                      setOpenFaq(isOpening ? index : null);
                      if (isOpening) {
                        posthog?.capture('invite_landing_faq_toggled', { faq_index: index, invite_id: inviteId });
                      }
                    }}
                  >
                    <span>{t(faq.qKey, { defaultValue: faq.q, ...(faq.vars || {}) })}</span>
                    {openFaq === index ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
                  </button>
                  {openFaq === index && (
                    <div className="faq-answer">
                      <p>{t(faq.aKey, { defaultValue: faq.a, ...(faq.vars || {}) })}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA: one path only. Hidden while the accept is in flight, since that visitor is
          about to be handed off to their map. */}
      {(!user || blocked) && (
        <div className="section-inner challenge-cta-wrapper">
          <div className="challenge-cta-banner">
            {blocked ? (
              <button type="button" className="challenge-cta-button" onClick={handleCreateClick}>
                {t('inviteLanding.makeOwn', 'Make my own map')}
                <IconChevronRight size={18} />
              </button>
            ) : (
              <button type="button" className="map-gen-strava-cta" onClick={handleStravaCta}>
                <img src={StravaConnectButton} alt={t('inviteLanding.connectStrava', 'Connect with Strava')} />
              </button>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default InviteLandingPage;
